/**
 * Centralised service definitions and pricing for DynamWorks.
 *
 * SINGLE SOURCE OF TRUTH — and as of DWEB-20 that is true rather than
 * aspirational. Services.js renders from SERVICES; ragService.js builds its
 * fallback knowledge-base pages from the same array. Before DWEB-20 this file
 * claimed the title in a comment while Services.js hard-coded its own cards, so
 * the page said $200/hr, this config said $150–$200/hour, and the chatbot's
 * knowledge base said something else again.
 *
 * A price string appears in exactly ONE place: PRICING below. Nothing else in
 * `src` may carry an OFFER PRICE. The invariant, in the form app-demos.spec.js
 * actually enforces:
 *
 *     grep -rlIE '\$[0-9][0-9,]*[/](hr|hour|month|project)' src
 *
 * (The slash is written as the class `[/]` rather than bare, because the bare
 * form puts `*` and `/` next to each other and ENDS THIS BLOCK COMMENT. It is
 * the same ERE and the command above is copy-pasteable as written; the spec
 * spells it without the class because a JS string has no such problem.)
 *
 * must return `config/servicesConfig.js` and nothing else. That is the
 * offer-price SHAPE: a figure with a per-unit.
 *
 * CORRECTED DWEB-103. This comment used to say `grep -rn '\$[0-9]' src` should
 * only ever point back here. THAT WAS NEVER TRUE and was not a measurement —
 * the bare pattern also matches the `$70M NIH grant` career fact in Proof.js
 * (a chip, its body, and a comment listing the chips) and two comments in
 * pricingAnswers.js and Chatbot.js that quote figures the MODEL invented, as
 * the record of why deterministic pricing exists. Six hits in three files,
 * all legitimate, all present before v6.4.0.
 *
 * The distinction matters because the loose form cannot be satisfied without
 * deleting true things: a career figure is not an offer price, and a comment
 * recording a hallucinated number is evidence, not copy. A guard that can only
 * be made green by damaging the file is a guard people learn to ignore.
 *
 * The chatbot's own copy lives in
 * `public/content/knowledgebase/sources/website-content.md` (DWEB-19), which is
 * prose for a language model rather than UI strings, and quotes the same
 * numbers.
 *
 * Copy is implemented verbatim from WEBSITE_COPY_DRAFT.md §3 (approved
 * 2026-08-24). The offers are ordered deliberately: Fractional / Interim CTO is
 * the flagship and comes first.
 *
 * PRICES RESTRUCTURED 2026-08-25 (DWEB-60), superseding the 2026-08-24 lock.
 * Every offer now carries a `cap` — the hour ceiling and what happens past it.
 * That field is the substance of DWEB-60, not decoration: at TaxMD $15,000/month
 * was contracted against 20-40 hrs/month and worked at 20-40 hrs/WEEK, an
 * effective $94-188/hr, because nothing capped it. A rate without a cap is
 * decorative. `cap` is a first-class field rather than prose inside
 * `description` because the chatbot reads these fields, and because a cap
 * buried in a paragraph is a cap nobody enforces.
 *
 * Applied Research moved OFF hourly entirely. At $175 it sat below consulting's
 * $200 despite being the rarer capability — an inversion — and the near-identical
 * hourly framing of Services 2 and 3 is a structural reason the chatbot
 * conflated them (DWEB-59). Fixed scope makes the offers different in kind.
 */

export const PRICING = {
  fractionalCto: {
    tier40: '$15,000/month up to 40 hours',
    tier60: '$20,000/month up to 60 hours',
    overage: '$350/hour',
    display: '$15,000–$20,000/month',
    cap:
      'Up to 40 hours a month at $15,000, or 60 at $20,000. Beyond that, $350/hour — ' +
      "I'll tell you before we get there, not after.",
  },
  consulting: {
    hourly: '$350/hr',
    retainer: '$1,500/month retainer',
    display: '$350/hr · $1,500/month retainer',
    cap:
      'The $1,500 monthly retainer includes four hours and priority response; ' +
      'anything beyond bills at the same $350/hour rate.',
  },
  research: {
    from: 'from $7,500',
    display: 'From $7,500 · fixed scope',
    cap:
      'Fixed scope, fixed price, from $7,500 — you buy the answer and the working code, ' +
      'not my hours. Organizational engagements are scoped separately.',
  },
  mvp: {
    project: 'From $10,000/project',
    display: 'From $10,000/project',
    cap: 'From $10,000, scoped to what regulated-ready actually requires for your domain.',
  },
  // DWEB-103. THE CAP IS SCOPE-SHAPED, NOT HOURS-SHAPED, and that is the one
  // thing to preserve if this entry is ever edited. DWEB-60's rule is that a
  // rate without a cap is decorative; it does not follow that every cap counts
  // hours. Here the runaway risk is scope creep across phone lines, languages,
  // workflow nodes and documents, not someone working too many hours — so the
  // ceiling is stated in the units the scope actually grows in. The monthly
  // hour is capped too, but it is the smaller of the two exposures.
  //
  // DWEB-109: $600 with the platform INCLUDED. The offer runs on DynamWorks'
  // own platform accounts, so the platform is a cost DynamWorks absorbs inside
  // the monthly, not a price it charges — which is why no platform figure
  // exists here or in REAL_FIGURES, and why the v6.4.0 sentence that put a
  // platform range on the client's accounts is deleted rather than reworded.
  // $500 including the platform was negative at today's maintenance load;
  // it survives only as a founding rate offered in conversation, and a
  // discount is not website copy.
  receptionist: {
    build: 'From $2,500 per line',
    monthly: '$600/month',
    overage: '$350/hour',
    display: 'From $2,500 · $600/month',
    cap:
      'From $2,500 for one phone line and one language — up to ten workflow ' +
      'nodes and three knowledge-base documents. The $600 monthly covers the ' +
      'platform, an hour of work, and the accuracy checks; beyond that hour, ' +
      '$350/hour.',
  },
};

/** The section intro, above the cards. */
/**
 * THE PHONE LINE (DWEB-140, v6.6.4). `+1 (626) 469-4941` is the production
 * line — phnum_4201m1jwck1gf83brrp5g0man2g4 on Twilio, assigned to the phone
 * agent — the product answering its maker's phone, NOT the browser demo
 * (DWEB-138, fictional, not built). ONE constant, ONE place: the display
 * form is written here and nowhere else in src/ (phone-number.spec.js greps
 * for the digits); the E.164 href, the schema.org form and the spoken form
 * (agent-kb/spoken.js) are derived from it. Three homes as ever: this file
 * renders it (the receptionist card, the footer, the launcher bubble),
 * website-content.md states it in its Contact line, and the agents'
 * practice document carries it to both surfaces — agent-kb/generate.mjs
 * refuses to generate if the first two disagree. The aria-label says what
 * the link DOES, not the digits again. Published under DWEB-137's limits
 * (100 calls a day, no bursting, three concurrent) and the policy's
 * thirty-day retention; the deletion flags behind that retention are
 * DWEB-152 and gate the release.
 */
const CONTACT_PHONE_DISPLAY = '+1 (626) 469-4941';
const CONTACT_PHONE_DIGITS = CONTACT_PHONE_DISPLAY.replace(/\D/g, '');
export const CONTACT_PHONE = {
  display: CONTACT_PHONE_DISPLAY,
  e164: `+${CONTACT_PHONE_DIGITS}`,
  href: `tel:+${CONTACT_PHONE_DIGITS}`,
  schema: `+${CONTACT_PHONE_DIGITS.slice(0, 1)}-${CONTACT_PHONE_DIGITS.slice(1, 4)}-${CONTACT_PHONE_DIGITS.slice(4, 7)}-${CONTACT_PHONE_DIGITS.slice(7)}`,
  label: 'Call DynamWorks',
};

export const SERVICES_INTRO =
  'Every engagement is led by the same accountable expert — no handoffs, no rotating team. ' +
  'Start where your situation is:';

/**
 * The five offers, in display order.
 *
 * `icon` is a key, not a node — this module is imported by ragService.js, which
 * has no business pulling in React. Services.js maps the key to an antd icon.
 *
 * FOUR UNTIL DWEB-103. The AI Phone Receptionist is the fifth and is the only
 * entry not drawn from WEBSITE_COPY_DRAFT.md §3; its copy comes from the
 * Confluence spec "Offer — AI Phone Receptionist". Adding it took the count
 * odd, which is why Services.js renders the last card full-width: the
 * two-column grid in componentLayout.js gives 2 + 2 + 1 and that lone third
 * row is the orphan DWEB-20's config exists to prevent. The grid config is
 * deliberately NOT changed — see the note in Services.js.
 */
const OFFERS = [
  {
    id: 'fractional-cto',
    icon: 'apartment',
    title: 'Fractional / Interim CTO',
    persona:
      'For domain-expert founders who need their product out the door without a full-time CTO.',
    description:
      "I own the architecture, the compliance posture, the delivery cadence, and the team's " +
      "technical guidance — and I translate between your domain's truth and the system being " +
      'built, in both directions.',
    proof:
      // DWEB-112: design verbs only, per the author's fact base — the platform
      // was live when the engagement began. Same sentence as the KB's Proven:
      // line; taxmd-facts.spec.js guards the verbs in both.
      'Proven: TaxMD — contracted by a CPA firm; stabilized and re-architected a live tax-compliance ' +
      'SaaS while leading a 24-person team.',
    price: PRICING.fractionalCto.display,
    cap: PRICING.fractionalCto.cap,
    pageIntro:
      'The first weeks are diagnostic: I review the architecture, work with the team, and listen '
      + 'to what is actually blocking them and what has already been tried. That produces two '
      + 'things \u2014 where the immediate benefit is, and an honest read on whether what exists can '
      + 'scale, stay reliable and stay maintainable. Where the plan involves real change I build a '
      + 'proof of concept of it rather than handing over a document, with a written review '
      + 'alongside if you need one. Engagements run for a contracted period and can be extended, '
      + 'month to month inside it, and either of us can exit if it is not working.',
    pageKeyword: 'fractional CTO',
    keywords: [
      'fractional CTO', 'interim CTO', 'Leadership as a Service', 'LaaS',
      'technical leadership', 'architecture', 'compliance posture',
      'delivery cadence', 'engineering management', 'executive',
      // DWEB-61: AI as the named substrate. Structured field, not retrieved
      // prose — a comma-separated capability list is safe here and toxic in
      // the knowledge base (see the header note in website-content.md).
      'AI CTO', 'fractional AI CTO', 'AI strategy', 'AI governance',
      'enterprise AI',
    ],
    summary:
      `DynamWorks Fractional / Interim CTO: ${PRICING.fractionalCto.display}. ` +
      'For domain-expert founders who need their product out the door without a full-time CTO. ' +
      "Ownership of architecture, compliance posture, delivery cadence, and the team's technical " +
      "guidance, plus translation between the domain's truth and the system being built, in both " +
      'directions. This is the offer also known as Leadership as a Service (LaaS). ' +
      `Pricing is tiered by hours: ${PRICING.fractionalCto.tier40}, or ${PRICING.fractionalCto.tier60}. ` +
      `Additional hours beyond the agreed ceiling are billed at ${PRICING.fractionalCto.overage}, ` +
      'and you are told before that ceiling is reached rather than after. ' +
      // DWEB-112: the assistant clause is the governing contract from the
      // author's fact base (resume-core.yaml, taxmd), replacing an embedding
      // claim the record had corrected; and every verb is a design verb, per
      // that record's TaxMD rule — stabilized, re-architected, specified,
      // designed — never the execution verbs it rules out. taxmd-facts.spec.js
      // guards the retired phrases and the verbs across this sentence, the
      // proof line above, the Proof.js card and the KB, and requires the
      // contract clause in each.
      // DWEB-117: the closing clause counted the document pipeline's gates
      // as a pair where the core has one write path with one gate — the same
      // miscount as the Proof.js card, in a home the ticket did not name.
      // Now the core's own clause ("gave" is the record's verb). "single write
      // path" sits inside ONE literal on purpose: it is the served-corpus
      // probe, and a phrase that spans a + is invisible to that grep.
      'Proven on TaxMD: contracted by a CPA firm; stabilized and re-architected a live tax-compliance ' +
      'SaaS while leading a 24-person team; specified a multi-intent AI assistant under one governing ' +
      'contract — deterministic engines and retrieval decide what is true, the LLM only routes and ' +
      'narrates — and gave canonical data a single write path: every value, human or AI, enters ' +
      'through one gate, and nothing AI-proposed lands without human confirmation.',
  },
  {
    id: 'consulting',
    icon: 'bulb',
    title: 'Technical Consulting & POC-to-Production Advisory',
    persona:
      'For technical leaders whose pilot works but whose production date keeps slipping.',
    description:
      'Architecture and scale reviews, production-readiness plans, regulated-delivery guidance, ' +
      'and the hard technical calls — at the exact point where R&D meets production and products ' +
      'usually stall.',
    price: PRICING.consulting.display,
    cap: PRICING.consulting.cap,
    pageIntro:
      'We discuss the problem and the objective, and you get advice on it. If you send enough '
      + 'detail before the call you often get more than advice: a partial or complete solution '
      + '\u2014 scripts included, where scripts are the answer \u2014 worked out from my own research into '
      + 'your problem, during the call or by email afterwards. Billed hourly, and no retainer is '
      + 'required to start.',
    pageKeyword: 'technical consulting',
    keywords: [
      'technical consulting', 'POC to production', 'production readiness',
      'architecture review', 'scale review', 'regulated delivery',
      // DWEB-227: 'GDPR', 'SOC 2' and 'HIPAA' were here and are GONE. They
      // rendered nowhere and reached no agent (measured: 0 occurrences in every
      // built HTML file, no reader in src/ or agent-kb/) — the array's ONLY
      // consumer is the pageKeyword membership check. So their whole effect was
      // to make `pageKeyword: 'HIPAA'` LEGAL, which would have shipped
      // "How HIPAA engagements run" as an <h2> through a green gate. Deleting
      // them closes that by construction; service-pages.spec.js keeps it closed.
      'advisory', 'retainer',
      // DWEB-61: this offer is where an AI pilot that will not ship lands.
      'AI consulting', 'AI POC to production', 'LLM in production', 'RAG',
      'agentic AI', 'MLOps', 'AI architecture review', 'AI readiness',
    ],
    summary:
      `DynamWorks Technical Consulting & POC-to-Production Advisory: ${PRICING.consulting.display}. ` +
      'For technical leaders whose pilot works but whose production date keeps slipping. ' +
      'Architecture and scale reviews, production-readiness plans, regulated-delivery guidance, ' +
      'and the hard technical calls — at the exact point where R&D meets production and products ' +
      'usually stall. This is the offer for an AI pilot that demonstrates well and will not ship: ' +
      'a model that works in a notebook but has no evaluation harness, no cost ceiling and no ' +
      'regulated-delivery story is the most common form of the stalled pilot, and carrying it ' +
      `across that gap is this engagement. Rates are ${PRICING.consulting.hourly}. ` +
      `A ${PRICING.consulting.retainer} includes four hours and priority response; work beyond ` +
      'those four hours bills at the same hourly rate.',
  },
  {
    id: 'research',
    icon: 'experiment',
    title: 'Applied-Research Sprint',
    // DWEB-60 B3: individuals and organisations are separated deliberately. The
    // from-$7,500 figure is an INDIVIDUAL researcher's entry point; leaving
    // "and teams" in the same breath let that number anchor an organisational
    // engagement it was never scoped for.
    persona:
      'For an individual researcher facing a question nobody has answered yet — technical or not.',
    description:
      'A research-grade attack on the problem, delivered as working code and a decision memo.',
    proof:
      'Proven: a 2005–06 Russian-language neuroscience thesis, translated, its analysis rebuilt ' +
      'on modern tooling, and its defense presentation prepared — for a non-technical researcher.',
    price: PRICING.research.display,
    cap: PRICING.research.cap,
    pageIntro:
      'The question usually arrives in one of three shapes: is this feasible at all, does the '
      + 'data actually say what someone claims, or is there an algorithm that makes this '
      + 'tractable. Work runs to a scope fixed before it starts and ends in two artifacts \u2014 code '
      + 'you can run yourself, and a memo stating what was found, how confident it is, and what '
      + 'more confidence would cost. A negative result delivered early is a successful sprint.',
    pageKeyword: 'applied research',
    keywords: [
      'applied research', 'research sprint', 'computational analysis',
      'decision memo', 'academic', 'feasibility', 'prototype', 'R&D',
      // DWEB-61
      'applied AI research', 'machine learning', 'model evaluation',
      'feasibility study',
    ],
    summary:
      `DynamWorks Applied-Research Sprint: ${PRICING.research.display}. ` +
      'For an individual researcher facing a question nobody has answered yet — technical or not. ' +
      'A research-grade attack on the problem, delivered as working code and a decision memo. ' +
      `It is priced ${PRICING.research.from} as a FIXED SCOPE engagement with no hourly rate: ` +
      'what is bought is the answer and the working code, not a number of hours. That from-price ' +
      'is an individual researcher\'s entry point; an organizational engagement is scoped ' +
      'separately and is not priced from this figure. ' +
      'Proven on a 2005–06 Russian-language neuroscience thesis: translated, its analysis rebuilt ' +
      'on modern tooling, and its defense presentation prepared for a non-technical researcher.',
  },
  {
    id: 'mvp',
    icon: 'rocket',
    title: 'Regulated-Ready MVP',
    persona: 'For founders with no team yet.',
    description:
      'The first version, built hands-on: privacy architecture, accessibility, and ' +
      'audit-friendliness from commit one — and a clean handoff the day your team arrives.',
    price: PRICING.mvp.display,
    cap: PRICING.mvp.cap,
    pageIntro:
      'The first version is a working product, built so that the next step is scaling it to '
      + 'production rather than rebuilding it. That comes from choosing technologies that fit the '
      + 'problem, keeping the structure modular with the smallest clean separation that works '
      + '\u2014 including the monolith-or-microservices call, which depends on the problem rather than '
      + 'on fashion \u2014 and keeping running costs sensible while getting to market quickly.',
    pageKeyword: 'MVP',
    keywords: [
      // DWEB-227: 'GDPR', 'SOC 2' and 'HIPAA' were here and are GONE — see the
      // consulting offer above for the measurement. This offer is the sharper
      // case: it is titled "Regulated-Ready MVP", so a framework name in an
      // <h2> here would read as the strongest possible claim, on the page best
      // placed to be believed. 'regulated' STAYS — it is this offer's own name
      // and the owner's approved copy; the rule is framework NAMES, never the
      // domain's adjectives.
      'MVP', 'regulated', 'privacy architecture', 'accessibility',
      'audit', 'handoff', 'product development',
      // DWEB-61
      'AI MVP', 'AI product development',
    ],
    summary:
      `DynamWorks Regulated-Ready MVP: ${PRICING.mvp.display}. ` +
      'For founders with no team yet. The first version, built hands-on: privacy architecture, ' +
      'accessibility, and audit-friendliness from commit one — and a clean handoff the day your ' +
      `team arrives. Pricing is ${PRICING.mvp.project}, scoped to what regulated-ready actually ` +
      'requires in your particular domain, which is why that figure is a floor rather than a quote.',
  },
  {
    // DWEB-103 — the fifth offer, and the first one whose proof a prospect
    // can dial.
    //
    // THREE THINGS THIS COPY MAY NOT SAY, each with a reason already paid for:
    //
    //   1. NOTHING ABOUT HIPAA, in the description or in `keywords`.
    //      complianceAnswers.js exists because v6.1.2 answered "Yes, the voice
    //      agent is HIPAA compliant" — a false regulatory claim about a
    //      healthcare client, live on production. That intercept runs upstream
    //      of retrieval, so it holds either way; the point is not to re-open
    //      the retrieval route that produced the sentence.
    //   2. NOT "handles patient calls". It triages and answers. Until DWEB-214
    //      the refusal list — books nothing, sees no records, gives no advice —
    //      was how that phrasing was refused; the booking and records clauses
    //      were scope DEFAULTS, not product limits, and are gone from this
    //      offer (they stay on Proof.js card B, which describes one delivered
    //      engagement, and on DynaMite, which describes the widget in use).
    //      receptionist-offer.spec.js's "handles patient calls" assertion now
    //      carries this rule alone.
    //   3. NO ABSOLUTE ZERO-HALLUCINATION CLAIM. "Every price and policy it
    //      speaks traces to a document you control" is true and defensible.
    //      "It never makes things up" is not: the same six-minute call
    //      disclosed a scope restriction about thirty turns later than policy
    //      required, and that miss is on record.
    id: 'ai-receptionist',
    icon: 'phone',
    title: 'AI Phone Receptionist',
    persona:
      'For a practice or small firm whose phone is answered by nobody, or by whoever happens ' +
      'to be free.',
    // DWEB-214 (owner's decision, 2026-09-14): "It books nothing, sees no
    // records" stated the receptionist's scope as a property of the product. It
    // is not one: what gets built depends on the client's use case and may
    // include calendar integration. The refusals were scope DEFAULTS, and they
    // are replaced — together with an interim "Its call flow is configured to
    // your business", which said configured without saying to what — by ONE
    // concrete sentence naming the same three options pageIntro names and the
    // agent's offers document has carried since DWEB-105. No fourth option, no
    // category word. Records are not an option and are not mentioned (note 1).
    // "gives no advice" stays: a liability boundary, not a configuration choice.
    // "Personalized"/"customized" are still NOT used: the concrete sentence is
    // stronger, and search matching is `keywords`' job.
    description:
      'It works out who is calling, answers from a source chosen for the question, and ends ' +
      'robocalls without reaching anyone. Every price and policy it speaks traces to a ' +
      'document you control, and it gives no advice. What happens after the answer is built to ' +
      'your requirement \u2014 your booking page, a message sent to you, or calendar integration so ' +
      'the call is actually booked.',
    // THE BOOKING CLAIM IS A CLAIM, NOT A COUNT, and it must stay that way.
    // An earlier draft said "five booking steps". That number was wrong in both
    // directions: the agent gave SEVEN discrete instructions on the live call,
    // and five was really the count of UI labels — Book Appointment, Functional
    // Initial Consult, See Schedule, Book Now, CONFIRM and BOOK — which itself
    // undercounts, because it also named "See Packages" correctly. Any number
    // here needs a counting rule nobody wrote down, so it will drift again.
    // "label by label" is true however you count and is the stronger claim.
    //
    // "six prices" and "four policies" DO stay as counts: both were checked
    // against the live knowledge base and are exact.
    proof:
      "Proven: a solo practice's phone line — six prices, four policies, and the " +
      'booking path quoted label by label on a live six-minute call.',
    price: PRICING.receptionist.display,
    cap: PRICING.receptionist.cap,
    pageIntro:
      'It answers for a lean business: what you do, what you offer, what it costs. Every price '
      + 'and policy it speaks traces back to a document you control, and it is built to stay '
      + 'inside that knowledge base rather than fill a gap with something plausible \u2014 that '
      + 'minimizes invention, it does not abolish it, and I will not claim otherwise. Where a '
      + 'caller wants to go further, what happens next is configured to your business: pointing '
      + 'them at your booking page, taking their name, email and reason and sending it to you, or '
      + 'integrating with your calendar so the call is actually booked.',
    pageKeyword: 'AI receptionist',
    // DWEB-140: the line under the number on this card. Three facts, in the
    // site's register — an AI answers and says so; calls are recorded; the
    // policy is linked (Services.js renders the link) — and nothing that
    // presents the live line as a demo or a sample. The number itself is
    // CONTACT_PHONE above; this string never carries it.
    callLine:
      'This is the line DynamWorks runs on. Call it: an AI answers, and says so. ' +
      'Calls are recorded and kept for thirty days; see the ',
    keywords: [
      'AI receptionist', 'voice agent', 'phone agent', 'call triage',
      'AI phone answering', 'after-hours calls', 'virtual receptionist',
      'small practice',
      // NO compliance framework belongs in this array. See note 1 above.
    ],
    summary:
      `DynamWorks AI Phone Receptionist: ${PRICING.receptionist.display}. ` +
      'For a practice or small firm whose phone is answered by nobody, or by whoever happens ' +
      'to be free. It works out who is calling — a new inquiry, an existing client, another ' +
      'provider or a robocall — answers from a source chosen for the question, and ends ' +
      'robocalls without reaching anyone. Every price and policy it speaks traces back to a ' +
      'document you control, and it gives no advice. What happens after the answer is built to ' +
      'your requirement \u2014 your booking page, a message sent to you, or calendar integration so ' +
      'the call is actually booked. ' +
      `Pricing: ${PRICING.receptionist.build}, covering one phone line and one language, ` +
      'up to ten workflow nodes and three knowledge-base documents. ' +
      `The ${PRICING.receptionist.monthly} covers the platform, one hour of work and the accuracy ` +
      `checks; beyond that hour the rate is ${PRICING.receptionist.overage}. ` +
      "Proven on a solo practice's phone line: six prices and four policies quoted correctly, " +
      'and the booking path given label by label, on a live six-minute call.',
  },
];

/**
 * The base path every service detail page sits under (DWEB-105).
 *
 * `path` is DERIVED from `id`, never retyped: the five ids already read as
 * slugs, and a hand-written path beside a hand-written id is two strings free
 * to disagree — the defect this file's whole header is about, one field along.
 * Deriving it means `/services/<id>` is true by construction.
 *
 * seo.js carries the same five paths as pageSEO KEYS, because it must stay
 * import-free (scripts/lib/importConfig.mjs refuses a config module with an
 * import, and the prerender loads seo.js from plain node). That second home is
 * held equal to this one by service-pages.spec.js — the DWEB-48 pattern: a
 * second copy is acceptable only with a test that fails when it drifts.
 *
 * `/services` ITSELF IS NOT A PAGE. DWEB-191 301s it to `/#services`, and the
 * homepage section is the index. Do not add one without retiring that redirect.
 */
export const SERVICES_BASE_PATH = '/services';

/**
 * The offers as the site renders them, each with the path of its own detail
 * page. `pageIntro` is the page body's prose and is deliberately NOT
 * `description`: prerenderContent.mjs FORBIDS any offer description on a route
 * page (that is what made `/`, `/blog` and `/privacy-policy` byte-identical
 * before DWEB-104), so the card text is the one thing these pages may not
 * reuse. `pageKeyword` is the search term the page's section headings are
 * built from, and it must be one of that offer's own `keywords` — asserted in
 * service-pages.spec.js, so a heading can never invent a term the offer does
 * not claim.
 *
 * THE FIVE pageIntro TEXTS ARE THE OWNER'S, dictated and approved 2026-09-11.
 * Do not rewrite, tighten or extend them. TWO WORDS were changed and only two:
 * "artefacts" -> "artifacts" (research) and "minimises" -> "minimizes"
 * (ai-receptionist), because DWEB-126 made US English the site's orthography
 * and already applied it to other owner-approved copy (rigour, organisational,
 * enquiry, judgement, catalogue). spelling.spec.js fails the gate on either
 * British form. Recorded on DWEB-105; one sed reverses it if the owner
 * prefers his spelling to the standard.
 *
 * THE ai-receptionist TEXT WAS WRITTEN AGAINST THE THREE PROHIBITIONS in that
 * offer's comment block. "That minimizes invention, it does not abolish it" is
 * the defensible form and replaces the owner's own "without hallucinating",
 * with his agreement. DO NOT TIGHTEN IT BACK. And BOOKING IS A CLIENT-SIDE
 * OPTION: the last sentence describes what a client's own deployment can be
 * configured to do. It is not a capability of the agents that answer for
 * DynamWorks — system-prompt-core.tmpl.md still tells those they cannot book.
 */
export const SERVICES = OFFERS.map((s) => ({ ...s, path: `${SERVICES_BASE_PATH}/${s.id}` }));

/**
 * Back-compat alias. ragService.js consumed SERVICES_META before DWEB-20 and
 * the shape it reads (id, title, summary, keywords) is unchanged.
 */
export const SERVICES_META = SERVICES;

export default SERVICES;
